/* ============================================================
   ZNAKI · controls.js
   Panel parametrów budowany ze schematu (config.js).
   Wszystkie suwaki działają na żywo, bez przebudowy sceny.

   Narzędzie deweloperskie — panel (wraz z jego stylami) jest
   tworzony wyłącznie w buildzie development, patrz ./index.js.
   ============================================================ */

import { CONTROL_SCHEMA, DEFAULTS, getParam, setParam, resetParams, exportParams } from './config';

const PANEL_ID = 'wj-playground-panel';
const STYLE_ID = 'wj-playground-panel-style';

const PANEL_CSS = `
#${PANEL_ID} {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: min(330px, 88vw); z-index: 1000;
  background: rgba(238, 228, 202, .93);
  backdrop-filter: blur(14px) saturate(1.1);
  -webkit-backdrop-filter: blur(14px) saturate(1.1);
  border-left: 1px solid #171207; color: #171207;
  font: 400 12px/1.4 "SF Mono", ui-monospace, Menlo, Consolas, monospace;
  transform: translateX(102%); transition: transform .4s cubic-bezier(.16,1,.3,1);
  display: flex; flex-direction: column; overflow: hidden; cursor: auto;
}
#${PANEL_ID}.is-open { transform: translateX(0); }
#${PANEL_ID} .panel-head {
  display: flex; align-items: baseline; justify-content: space-between;
  padding: 18px 20px 12px; border-bottom: 1px solid rgba(23,18,7,.38); flex: 0 0 auto;
}
#${PANEL_ID} .panel-head h2 { font-size: 11px; letter-spacing: .26em; text-transform: uppercase; margin: 0; }
#${PANEL_ID} .panel-head .seed { font-size: 10px; letter-spacing: .1em; opacity: .6; margin-left: auto; }
#${PANEL_ID} .panel-close {
  appearance: none; background: transparent; border: 0; padding: 0;
  margin-left: 14px; color: #171207; font: inherit; font-size: 16px; line-height: 1;
  opacity: .55; cursor: pointer; transition: opacity .15s, color .15s;
}
#${PANEL_ID} .panel-close:hover { opacity: 1; color: #F4761C; }
#${PANEL_ID} .panel-body { flex: 1 1 auto; overflow-y: auto; padding: 4px 20px 16px; }
#${PANEL_ID} .panel-group { padding: 14px 0 4px; }
#${PANEL_ID} .panel-group + .panel-group { border-top: 1px solid rgba(23,18,7,.12); }
#${PANEL_ID} .panel-group h3 {
  font-size: 9px; letter-spacing: .3em; text-transform: uppercase;
  opacity: .5; margin: 0 0 12px; font-weight: 700;
}
#${PANEL_ID} .ctrl { margin-bottom: 13px; }
#${PANEL_ID} .ctrl-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 5px; }
#${PANEL_ID} .ctrl-row label { font-size: 10px; letter-spacing: .1em; text-transform: uppercase; margin: 0; cursor: pointer; }
#${PANEL_ID} .ctrl-row output { font-size: 10px; opacity: .65; font-variant-numeric: tabular-nums; }
#${PANEL_ID} input[type="range"] {
  -webkit-appearance: none; appearance: none; width: 100%; height: 18px; background: transparent; margin: 0;
}
#${PANEL_ID} input[type="range"]::-webkit-slider-runnable-track { height: 1px; background: #171207; }
#${PANEL_ID} input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none; width: 11px; height: 11px; margin-top: -5px;
  background: #171207; border-radius: 0; transition: background .15s, transform .15s;
}
#${PANEL_ID} input[type="range"]:hover::-webkit-slider-thumb { background: #F4761C; transform: scale(1.25); }
#${PANEL_ID} input[type="range"]::-moz-range-track { height: 1px; background: #171207; }
#${PANEL_ID} input[type="range"]::-moz-range-thumb { width: 11px; height: 11px; background: #171207; border: 0; border-radius: 0; }
#${PANEL_ID} .panel-foot {
  flex: 0 0 auto; display: flex; gap: 6px;
  padding: 14px 20px 18px; border-top: 1px solid rgba(23,18,7,.38);
}
#${PANEL_ID} .pbtn {
  flex: 1 1 0; min-width: 0; appearance: none; background: transparent;
  border: 1px solid rgba(23,18,7,.38);
  color: #171207; font: inherit; font-size: 10px; letter-spacing: .1em;
  text-transform: uppercase; padding: 9px 4px; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  transition: background .15s, color .15s, border-color .15s;
}
#${PANEL_ID} .pbtn:hover { background: #171207; color: #EBDFC3; border-color: #171207; }
#${PANEL_ID} .pbtn--accent:hover { background: #F4761C; border-color: #F4761C; color: #171207; }
#${PANEL_ID}-toggle {
  position: fixed; right: 0; bottom: 40%; z-index: 999;
  appearance: none; border: 1px solid rgba(23,18,7,.38); border-right: 0;
  background: rgba(238,228,202,.86); color: #171207; cursor: pointer;
  font: 700 9px/1 "SF Mono", ui-monospace, Menlo, monospace;
  letter-spacing: .2em; text-transform: uppercase; padding: 10px 8px;
  writing-mode: vertical-rl;
}
`;

class Controls {

  /**
   * @param {object} hooks — { onReseed(), getSeed() }
   */
  constructor(hooks) {
    this.hooks = hooks;
    this._outputs = new Map();   /* path → { out, step } */
    this._inputs = new Map();    /* path → <input>       */
    this._injectStyle();
    this._build();
  }

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = PANEL_CSS;
    document.head.appendChild(s);
  }

  _build() {
    const r = document.createElement('aside');
    r.id = PANEL_ID;
    this.root = r;

    /* nagłówek */
    const head = document.createElement('div');
    head.className = 'panel-head';
    head.innerHTML = '<h2>Parametry</h2>';
    this._seedEl = document.createElement('span');
    this._seedEl.className = 'seed';
    head.appendChild(this._seedEl);

    /* zamknięcie panelu (to samo co Esc / klawisz C) */
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'panel-close';
    close.innerHTML = '&times;';
    close.title = 'Zamknij panel (Esc)';
    close.setAttribute('aria-label', 'Zamknij panel parametrów');
    close.addEventListener('click', () => this.toggle(false));
    head.appendChild(close);

    r.appendChild(head);

    /* grupy suwaków */
    const body = document.createElement('div');
    body.className = 'panel-body';

    for (const group of CONTROL_SCHEMA) {
      const g = document.createElement('section');
      g.className = 'panel-group';
      const h = document.createElement('h3');
      h.textContent = group.group;
      g.appendChild(h);

      for (const item of group.items) g.appendChild(this._makeSlider(item));
      body.appendChild(g);
    }
    r.appendChild(body);

    /* stopka */
    const foot = document.createElement('div');
    foot.className = 'panel-foot';

    const bNew = document.createElement('button');
    bNew.type = 'button';
    bNew.className = 'pbtn pbtn--accent';
    bNew.textContent = 'Odśwież';
    bNew.title = 'Nowa kompozycja (nowe ziarno)';
    bNew.addEventListener('click', () => this.hooks.onReseed());

    const bExport = document.createElement('button');
    bExport.type = 'button';
    bExport.className = 'pbtn';
    bExport.textContent = 'Eksport';
    bExport.title = 'Zapisz bieżące ustawienia jako plik JSON';
    bExport.addEventListener('click', () => this._export(bExport));

    const bReset = document.createElement('button');
    bReset.type = 'button';
    bReset.className = 'pbtn';
    bReset.textContent = 'Reset';
    bReset.title = 'Przywróć wartości domyślne';
    bReset.addEventListener('click', () => { resetParams(); this.refresh(); });

    foot.append(bNew, bExport, bReset);
    r.appendChild(foot);

    document.body.appendChild(r);

    /* uchwyt otwierający panel (skrót klawiszowy: C) */
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.id = PANEL_ID + '-toggle';
    toggle.textContent = 'parametry';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.addEventListener('click', () => this.toggle());
    document.body.appendChild(toggle);
    this._toggleEl = toggle;

    this.refresh();
  }

  _makeSlider({ path, label, min, max, step }) {
    const wrap = document.createElement('div');
    wrap.className = 'ctrl';

    const row = document.createElement('div');
    row.className = 'ctrl-row';

    const id = 'ctrl-' + path.replace('.', '-');
    const lab = document.createElement('label');
    lab.htmlFor = id;
    lab.textContent = label;

    const out = document.createElement('output');

    row.append(lab, out);

    const input = document.createElement('input');
    input.type = 'range';
    input.id = id;
    input.min = min;
    input.max = max;
    input.step = step;
    input.value = getParam(path);

    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      setParam(path, v);
      out.value = this._fmt(v, step);
    });

    /* dwuklik w etykietę — powrót do wartości domyślnej */
    lab.addEventListener('dblclick', () => {
      const [g, k] = path.split('.');
      setParam(path, DEFAULTS[g][k]);
      this.refresh();
    });

    this._outputs.set(path, { out, step });
    this._inputs.set(path, input);

    wrap.append(row, input);
    return wrap;
  }

  /* --- eksport ustawień do pliku JSON -----------------------
     Zrzut zawiera pełne PARAMS oraz skrót "changed" — same odchylenia
     od domyślnych, gotowe do przepisania do config.js. */
  _export(btn) {
    const seed = this.hooks.getSeed ? this.hooks.getSeed() : null;
    const data = exportParams(seed ? { seed } : {});
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `znaki-${seed || 'params'}-${this._stamp()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    /* krótkie potwierdzenie na przycisku */
    if (!btn) return;
    clearTimeout(this._exportT);
    btn.textContent = 'Zapisano';
    this._exportT = setTimeout(() => { btn.textContent = 'Eksport'; }, 1400);
  }

  /* RRRRMMDD-GGMM, czas lokalny */
  _stamp() {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`
      + `-${p(d.getHours())}${p(d.getMinutes())}`;
  }

  _fmt(v, step) {
    const dec = step >= 1 ? 0 : (step >= 0.005 ? 2 : 3);
    return Number(v).toFixed(dec);
  }

  /* odśwież wartości (po resecie / regeneracji) */
  refresh() {
    for (const [path, { out, step }] of this._outputs) {
      const v = getParam(path);
      out.value = this._fmt(v, step);
      this._inputs.get(path).value = v;
    }
    if (this.hooks.getSeed) this._seedEl.textContent = 'ziarno ' + this.hooks.getSeed();
  }

  toggle(force) {
    const open = force !== undefined ? force : !this.root.classList.contains('is-open');
    this.root.classList.toggle('is-open', open);
    this._toggleEl.setAttribute('aria-expanded', String(open));
    return open;
  }

  get isOpen() { return this.root.classList.contains('is-open'); }

  destroy() {
    clearTimeout(this._exportT);
    this.root.remove();
    this._toggleEl.remove();
    this._outputs.clear();
    this._inputs.clear();
  }
}

export default Controls;
